import React, { useState } from "react";
import axios from "axios";
import {
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody,
  Button, Flex, Box, Text, Select, FormControl, FormLabel, Input, InputGroup, InputLeftAddon, useToast, useDisclosure, Spinner, Portal,
} from "@chakra-ui/react";

const API = process.env.REACT_APP_API_URL;

/**
 * The page / retailer / supplier access editors as POPOVERS (spec:
 * users.master-admins.md §then-for-the-2) — no navigation, so the table's
 * scroll position is untouched. Each fetches lazily when opened and saves via
 * the same endpoints the old Edit* pages used. Billing follows the same shape.
 */

const TinyToggle = ({ on, onClick, children }) => (
  <Button size="xs" variant={on ? "solid" : "outline"} colorScheme={on ? "green" : "gray"} mr={1} mb={1} onClick={onClick}>
    {children}
  </Button>
);

const GrantPopover = ({ trigger, header, onOpenFetch, onSave, children, width = "340px" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const open = async () => {
    onOpen();
    if (!onOpenFetch) return;
    setLoading(true);
    try {
      await onOpenFetch();
    } catch (error) {
      toast({ title: "Failed to load", description: error?.response?.data?.message || error?.message, status: "error", duration: 5000, isClosable: true });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave();
      onClose();
    } catch (error) {
      toast({ title: "Failed to save", description: error?.response?.data?.message || error?.message, status: "error", duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover isOpen={isOpen} onOpen={open} onClose={onClose} placement="left" isLazy>
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <Portal>
        <PopoverContent w={width}>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontSize="sm" fontWeight="600">{header}</PopoverHeader>
          <PopoverBody>
            {loading ? (
              <Flex justify="center" py={4}><Spinner size="sm" /></Flex>
            ) : (
              <>
                {children}
                <Flex justify="flex-end" gap={2} mt={3}>
                  <Button size="xs" variant="ghost" onClick={onClose}>Cancel</Button>
                  <Button size="xs" colorScheme="teal" onClick={save} isLoading={saving}>Save</Button>
                </Flex>
              </>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// ── Pages ───────────────────────────────────────────────────────────────────
// Grantable groups per type — mirrors the backend's allowedPagesForType, which
// also filters server-side, so nothing outside the set can stick. Partner-org
// accounts (org_code ≠ SPEARMGMT) never get My Profile.
const pageGroupsFor = (user, sets) => {
  const spearOnly = (items) =>
    user.org_code && user.org_code !== "SPEARMGMT" ? (items || []).filter((p) => p !== "MyProfile") : items || [];
  if (user.user_type === "CAM") {
    return [
      { label: "Report pages", items: spearOnly(sets.reportPages) },
      { label: "Admin capabilities (scoped to their suppliers/retailers)", items: sets.camAdminPages },
    ];
  }
  if (user.user_type === "org_admin") return [{ label: "Pages", items: spearOnly(sets.orgAdminPages) }];
  if (user.user_type === "admin") return [{ label: "Pages", items: sets.pages }];
  return [{ label: "Report pages", items: spearOnly(sets.reportPages) }];
};

export const PagesPopover = ({ user, pageSets, onSaved }) => {
  const [selected, setSelected] = useState([]);
  const fetchGrants = async () => {
    const res = await axios.post(`${API}/admin/get-user-pageAccess`, { userid: user.userid });
    setSelected((res.data?.data?.page_access || []).map((p) => p.page_name));
  };
  const save = async () => {
    await axios.post(`${API}/admin/upd-user-pageAccess`, { userid: user.userid, pages: selected });
    if (onSaved) onSaved();
  };
  const toggle = (p) => setSelected((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  return (
    <GrantPopover trigger={<Button size="xs" variant="ghost">Pages</Button>} header={`Page access — ${user.fname}`} onOpenFetch={fetchGrants} onSave={save}>
      {pageGroupsFor(user, pageSets).map((group) => (
        <Box key={group.label} mb={2}>
          <Text fontSize="xs" color="ink.500" mb={1}>{group.label}</Text>
          <Box>
            {(group.items || []).map((p) => (
              <TinyToggle key={p} on={selected.includes(p)} onClick={() => toggle(p)}>{p}</TinyToggle>
            ))}
          </Box>
        </Box>
      ))}
    </GrantPopover>
  );
};

// ── Retailers ────────────────────────────────────────────────────────────────
const RETAILERS = ["PNP", "CHK", "DIS", "MAK", "CLI", "MED", "PET"];

export const RetailersPopover = ({ user, onSaved }) => {
  const [access, setAccess] = useState({});
  const [defaultRetailer, setDefaultRetailer] = useState("");
  const fetchGrants = async () => {
    const res = await axios.post(`${API}/admin/get-user-retailerAccess`, { userid: user.userid });
    const { retailer_access, user: u } = res.data.data;
    setAccess(retailer_access.length ? retailer_access[0] : {});
    setDefaultRetailer(u.default_retailer || "");
  };
  const save = async () => {
    await axios.post(`${API}/admin/upd-user-retailerAccess`, { userid: user.userid, default_retailer: defaultRetailer, retailerAccess: access });
    if (onSaved) onSaved();
  };
  const toggle = (r) => setAccess((prev) => ({ ...prev, [r]: !prev[r] }));

  return (
    <GrantPopover trigger={<Button size="xs" variant="ghost">Retailers</Button>} header={`Retailer access — ${user.fname}`} onOpenFetch={fetchGrants} onSave={save}>
      <Box mb={2}>
        {RETAILERS.map((r) => (
          <TinyToggle key={r} on={!!access[r.toLowerCase()]} onClick={() => toggle(r.toLowerCase())}>{r}</TinyToggle>
        ))}
      </Box>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Default retailer</FormLabel>
        <Select size="xs" value={defaultRetailer || ""} onChange={(e) => setDefaultRetailer(e.target.value)}>
          <option value="">—</option>
          {RETAILERS.filter((r) => access[r.toLowerCase()]).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </Select>
      </FormControl>
    </GrantPopover>
  );
};

// ── Suppliers ────────────────────────────────────────────────────────────────
export const SuppliersPopover = ({ user, onSaved }) => {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [defaultSupplier, setDefaultSupplier] = useState("");
  const fetchGrants = async () => {
    const res = await axios.post(`${API}/admin/get-user-supplierAccess`, { userid: user.userid });
    const { supplier_access, suppliers, user: u } = res.data.data;
    setSelected(supplier_access.map((s) => s.suppliercode));
    setAvailable(suppliers.map((s) => s.suppliercode));
    setDefaultSupplier(u.default_supplier || "");
  };
  const save = async () => {
    await axios.post(`${API}/admin/upd-user-supplierAccess`, { userid: user.userid, default_supplier: defaultSupplier, suppliers: selected });
    if (onSaved) onSaved();
  };
  const toggle = (s) => setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <GrantPopover trigger={<Button size="xs" variant="ghost">Suppliers</Button>} header={`Supplier access — ${user.fname}`} onOpenFetch={fetchGrants} onSave={save} width="420px">
      <Box maxH="260px" overflowY="auto" mb={2}>
        {available.map((s) => (
          <TinyToggle key={s} on={selected.includes(s)} onClick={() => toggle(s)}>{s}</TinyToggle>
        ))}
      </Box>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Default supplier</FormLabel>
        <Select size="xs" value={defaultSupplier || ""} onChange={(e) => setDefaultSupplier(e.target.value)}>
          <option value="">—</option>
          {selected.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
      </FormControl>
    </GrantPopover>
  );
};

// ── Billing ──────────────────────────────────────────────────────────────────
// Amount is stored in cents (users.subscription_amount); edited in Rands here.
export const BillingPopover = ({ user, trigger, onSaved }) => {
  const [amount, setAmount] = useState(((Number(user.subscription_amount) || 0) / 100).toString());
  const save = async () => {
    await axios.post(`${API}/admin/upd-subscription_amount`, { userid: user.userid, subscription_amount: Math.round((parseFloat(amount) || 0) * 100) });
    if (onSaved) onSaved();
  };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

  return (
    <GrantPopover trigger={trigger} header={`Billing — ${user.fname}`} onSave={save} width="280px">
      <FormControl mb={2}>
        <FormLabel fontSize="xs" mb={1}>Monthly amount</FormLabel>
        <InputGroup size="xs">
          <InputLeftAddon>R</InputLeftAddon>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </InputGroup>
      </FormControl>
      <Text fontSize="xs" color="ink.500">Last paid: {fmt(user.date_last_paid)}</Text>
      <Text fontSize="xs" color="ink.500">Subscribed since: {fmt(user.subscription_sdate)}</Text>
      <Text fontSize="xs" color="ink.500">Account created: {fmt(user.createddate)}</Text>
    </GrantPopover>
  );
};
